// src/components/Notifications/ToastNotification.tsx
import React, { useEffect, useState } from 'react';
import { Notification } from '../../context/NotificationContext';

interface ToastNotificationProps {
  notification: Notification;
  onClose: (id: string) => void;
  index: number;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({ 
  notification, 
  onClose,
  index
}) => {
  const [isExiting, setIsExiting] = useState(false);
  
  // Determine styles based on notification type
  const getTypeStyles = () => {
    switch (notification.type) {
      case 'success':
        return 'bg-green-50 border-green-400 text-green-800';
      case 'error':
        return 'bg-red-50 border-red-400 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-400 text-yellow-800';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-400 text-blue-800';
    }
  };

  // Get icon based on notification type
  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return (
          <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  // Set up auto-dismiss
  useEffect(() => {
    if (!notification.duration) return undefined;
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => {
        onClose(notification.id);
      }, 300); // Animation duration
    }, notification.duration - 300); // Start exit animation before duration ends

    return () => clearTimeout(timer);
  }, [notification, onClose]);

  // Handle manual close
  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(notification.id);
    }, 300); // Animation duration
  };

  return (
    <div 
      className={`
        fixed right-4 shadow-lg rounded-lg border-l-4 p-3 mb-3 flex items-center justify-between
        transition-all duration-300 transform ${getTypeStyles()}
        ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'}
      `}
      style={{ 
        zIndex: 1000, 
        top: `${4 + (index * 80)}px`,
        width: '360px',
        maxWidth: 'calc(100vw - 32px)'
      }}
    >
      <div className="flex items-center">
        <div className="shrink-0 mr-3">
          {getIcon()}
        </div>
        <div>
          <p className="text-sm font-medium">{notification.message}</p>
        </div>
      </div>
      <button 
        onClick={handleClose}
        className="ml-4 text-gray-400 hover:text-gray-900 focus:outline-hidden"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  );
};

export default ToastNotification;